/**
 * UnoBoard — Discard pile, draw pile, player hand (fan), opponent card backs,
 * direction indicator, and color chooser for wilds.
 */
import React, { useState } from 'react';
import {
  UnoBoardProps, UnoCard, UnoColor, VisibleUnoPlayerState,
  UNO_COLORS, UNO_COLOR_HEX,
} from '../types';
import './UnoBoard.css';

// ============================================================================
// Card Display Component
// ============================================================================

const UnoCardDisplay: React.FC<{
  card: UnoCard;
  faceDown?: boolean;
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  highlighted?: boolean;
  disabled?: boolean;
}> = ({ card, faceDown, size = 'medium', onClick, highlighted, disabled }) => {
  const sizeClass = `uno-card--${size}`;
  const colorClass = faceDown ? 'uno-card--back' : `uno-card--${card.color || 'wild'}`;

  const getCardLabel = (): string => {
    if (faceDown) return '';
    switch (card.type) {
      case 'number': return String(card.value ?? '');
      case 'skip': return '⊘';
      case 'reverse': return '⟲';
      case 'draw-two': return '+2';
      case 'wild': return '★';
      case 'wild-draw-four': return '+4';
      default: return '';
    }
  };

  const getCardTitle = (): string => {
    if (faceDown) return 'Card back';
    const colorName = card.color ? card.color.charAt(0).toUpperCase() + card.color.slice(1) : 'Wild';
    switch (card.type) {
      case 'number': return `${colorName} ${card.value}`;
      case 'skip': return `${colorName} Skip`;
      case 'reverse': return `${colorName} Reverse`;
      case 'draw-two': return `${colorName} Draw Two`;
      case 'wild': return 'Wild';
      case 'wild-draw-four': return 'Wild Draw Four';
      default: return '';
    }
  };

  const bgColor = faceDown
    ? undefined
    : card.color
      ? UNO_COLOR_HEX[card.color]
      : '#333';

  return (
    <button
      className={`uno-card ${sizeClass} ${colorClass} ${highlighted ? 'uno-card--highlighted' : ''} ${disabled ? 'uno-card--disabled' : ''}`}
      onClick={onClick}
      disabled={disabled || faceDown}
      style={bgColor ? { '--card-bg': bgColor } as React.CSSProperties : undefined}
      aria-label={getCardTitle()}
      title={getCardTitle()}
    >
      <span className="uno-card__label">{getCardLabel()}</span>
      {(card.type === 'wild' || card.type === 'wild-draw-four') && !faceDown && (
        <span className="uno-card__wild-indicator">
          {UNO_COLORS.map(c => (
            <span key={c} className="uno-card__wild-dot" style={{ background: UNO_COLOR_HEX[c] }} />
          ))}
        </span>
      )}
    </button>
  );
};

// ============================================================================
// Color Chooser Modal
// ============================================================================

const ColorChooser: React.FC<{
  onChoose: (color: UnoColor) => void;
}> = ({ onChoose }) => (
  <div className="uno-color-chooser" role="dialog" aria-label="Choose a color">
    <div className="uno-color-chooser__content">
      <h3 className="uno-color-chooser__title">Choose a Color</h3>
      <div className="uno-color-chooser__options">
        {UNO_COLORS.map(color => (
          <button
            key={color}
            className={`uno-color-chooser__btn uno-color-chooser__btn--${color}`}
            onClick={() => onChoose(color)}
            aria-label={`Choose ${color}`}
            style={{ background: UNO_COLOR_HEX[color] }}
          >
            {color.charAt(0).toUpperCase() + color.slice(1)}
          </button>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================================
// Opponent Hand Display
// ============================================================================

const OpponentHand: React.FC<{
  player: VisibleUnoPlayerState;
  isCurrentTurn: boolean;
}> = ({ player, isCurrentTurn }) => (
  <div className={`uno-opponent ${isCurrentTurn ? 'uno-opponent--active' : ''}`}>
    <div className="uno-opponent__name">
      {player.name}
      {player.hasCalledUno && <span className="uno-opponent__uno-badge">UNO!</span>}
    </div>
    <div className="uno-opponent__cards">
      {Array.from({ length: Math.min(player.cardCount, 10) }).map((_, i) => (
        <div key={i} className="uno-opponent__card-back" />
      ))}
      {player.cardCount > 10 && (
        <span className="uno-opponent__overflow">+{player.cardCount - 10}</span>
      )}
    </div>
    <div className="uno-opponent__count">{player.cardCount} cards</div>
  </div>
);

// ============================================================================
// UnoBoard Component
// ============================================================================

const UnoBoard: React.FC<UnoBoardProps> = ({
  gameState,
  currentPlayerId,
  onPlayCard,
  onDrawCard,
  onCallUno,
  isCurrentPlayerTurn,
  playerHand,
}) => {
  const [pendingWildCard, setPendingWildCard] = useState<UnoCard | null>(null);
  const [showColorChooser, setShowColorChooser] = useState(false);

  const opponents = gameState.players.filter(p => p.id !== currentPlayerId);
  const currentPlayer = gameState.players.find(p => p.id === currentPlayerId);

  const handleCardClick = (card: UnoCard) => {
    if (!isCurrentPlayerTurn) return;

    if (card.type === 'wild' || card.type === 'wild-draw-four') {
      setPendingWildCard(card);
      setShowColorChooser(true);
      return;
    }

    onPlayCard(card);
  };

  const handleColorChosen = (color: UnoColor) => {
    if (pendingWildCard) {
      onPlayCard(pendingWildCard, color);
      setPendingWildCard(null);
      setShowColorChooser(false);
    }
  };

  // Determine which cards in hand are playable
  const playableCardIds = new Set<string>();
  if (isCurrentPlayerTurn && playerHand) {
    // Simple check: we'll mark cards that match color/number/type or are wild
    const topCard = gameState.topCard;
    const activeColor = gameState.chosenColor || topCard.color;

    for (const card of playerHand) {
      if (gameState.drawStack > 0) {
        if (card.type === 'draw-two') playableCardIds.add(card.id);
        continue;
      }
      if (card.type === 'wild' || card.type === 'wild-draw-four') {
        playableCardIds.add(card.id);
      } else if (card.color === activeColor) {
        playableCardIds.add(card.id);
      } else if (card.type === 'number' && topCard.type === 'number' && card.value === topCard.value) {
        playableCardIds.add(card.id);
      } else if (card.type !== 'number' && card.type === topCard.type) {
        playableCardIds.add(card.id);
      }
    }
  }

  const directionSymbol = gameState.direction === 'clockwise' ? '↻' : '↺';

  return (
    <div className="uno-board" role="region" aria-label="UNO game board">
      {/* Opponents */}
      <div className="uno-board__opponents">
        {opponents.map(opp => (
          <OpponentHand
            key={opp.id}
            player={opp}
            isCurrentTurn={gameState.players[gameState.currentPlayerIndex]?.id === opp.id}
          />
        ))}
      </div>

      {/* Center Area: Draw pile + Discard pile + Direction */}
      <div className="uno-board__center">
        {/* Draw Pile */}
        <button
          className="uno-board__draw-pile"
          onClick={onDrawCard}
          disabled={!isCurrentPlayerTurn}
          aria-label={`Draw pile (${gameState.drawPileCount} cards). Click to draw a card.`}
        >
          <div className="uno-card uno-card--back uno-card--medium">
            <span className="uno-card__label">UNO</span>
          </div>
          <span className="uno-board__pile-count">{gameState.drawPileCount}</span>
        </button>

        {/* Discard Pile */}
        <div className="uno-board__discard-pile" aria-label="Discard pile">
          <UnoCardDisplay card={gameState.topCard} size="large" />
          {gameState.chosenColor && (gameState.topCard.type === 'wild' || gameState.topCard.type === 'wild-draw-four') && (
            <div
              className="uno-board__chosen-color"
              style={{ background: UNO_COLOR_HEX[gameState.chosenColor] }}
              aria-label={`Chosen color: ${gameState.chosenColor}`}
            />
          )}
        </div>

        {/* Direction Indicator */}
        <div className="uno-board__direction" aria-label={`Direction: ${gameState.direction}`}>
          <span className="uno-board__direction-symbol">{directionSymbol}</span>
        </div>

        {/* Draw Stack Indicator */}
        {gameState.drawStack > 0 && (
          <div className="uno-board__draw-stack" role="status">
            +{gameState.drawStack} cards pending
          </div>
        )}
      </div>

      {/* Player's Hand */}
      <div className="uno-board__player-area">
        <div className="uno-board__player-info">
          <span className="uno-board__player-name">
            {currentPlayer?.name || 'You'}
            {currentPlayer?.hasCalledUno && <span className="uno-board__uno-badge">UNO!</span>}
          </span>
          {isCurrentPlayerTurn && (
            <span className="uno-board__turn-indicator" role="status" aria-live="polite">
              🎯 Your Turn
            </span>
          )}
        </div>

        <div className="uno-board__hand" role="list" aria-label="Your hand">
          {playerHand.map((card, index) => {
            const isPlayable = playableCardIds.has(card.id);
            return (
              <div
                key={card.id}
                className="uno-board__hand-card"
                style={{ '--card-index': index, '--total-cards': playerHand.length } as React.CSSProperties}
                role="listitem"
              >
                <UnoCardDisplay
                  card={card}
                  size="medium"
                  onClick={() => handleCardClick(card)}
                  highlighted={isPlayable && isCurrentPlayerTurn}
                  disabled={!isPlayable || !isCurrentPlayerTurn}
                />
              </div>
            );
          })}
        </div>

        {/* UNO Call Button */}
        {isCurrentPlayerTurn && playerHand.length <= 2 && (
          <button
            className="uno-board__uno-btn"
            onClick={onCallUno}
            aria-label="Call UNO"
          >
            UNO!
          </button>
        )}
      </div>

      {/* Color Chooser Modal */}
      {showColorChooser && (
        <ColorChooser onChoose={handleColorChosen} />
      )}
    </div>
  );
};

export { UnoBoard, UnoCardDisplay, ColorChooser };
export default UnoBoard;
